import React, { useEffect, useMemo, useState } from 'react';
import {
  DynamicExperienceProvider,
  type DynamicExperienceVariant,
} from './providers/DynamicExperienceProvider';
import {
  ObservedFormStateProvider,
  type ObservedFormStateChange,
} from './providers/ObservedFormState';
import { Experience } from './components/Experience';
import { ExperiencePlacement } from './components/Experience/ExperiencePlacement';
import { ExperienceConfigProvider } from './providers/ExperienceConfigProvider';
import { TaxonomyProvider } from './providers/TaxonomyProvider';
import {
  DCS_NAMESPACE,
  fetchTaxonomySeed,
  type DcsKey,
  type TaxonomySeed,
} from './utils/taxonomy/fetchTaxonomy';
import {
  applyEntryPointDcsDefaults,
  getEntryPointConfigFromSteps,
} from './utils/configurable-step/extractEntryPointConfig';
import { buildBoundComponentIndex } from './utils/configurable-step/findBoundComponent';
import { getFlow } from './services/api/flowService';
import { resolveFlowTenantId } from './services/api/resolveFlowTenantId';
import type { ExperienceConfig, SiteConfig } from './types/config';
import type { Environment } from './config/environments';
import {
  getApiConfigForEnv,
  resolveLeadApiBaseUrl,
  resolveLocationInferenceApiUrl,
  resolveProductApiBffOrigin,
} from './config/environments';
import type {
  SlotPlacementId,
  DCSContext,
  HostHeadline,
  FlowContextParams,
  Step,
  StepResult,
  DecisionProfileResponse,
  CustomDimensions,
} from './types/types';
import type { FlowState } from './providers/dynamicExperienceReducer';
import type { RequestContextSnapshot } from './requestContextSnapshot';
import { uuid } from './shared/utils';
import type { EmbeddedDecisions } from './utils/decisions/embeddedDecisions';
import {
  newRelicCustomAttribute,
  newRelicNoticeError,
} from '@de/utils/newRelicActions';

/**
 * Per-placement fallback flow IDs used when no `forcedFlowId` (CMS/Monarch)
 * resolves. Each placement falls back to its canonical published flow rather
 * than the generic `default-${placement}` slug. Placements not listed here
 * keep the `default-${placement}` fallback.
 */
const PLACEMENT_FALLBACK_FLOW_IDS: Partial<Record<SlotPlacementId, string>> = {
  aboveTheFold: 'personalized-dcs-v2-dsl',
  belowTheFold: 'editorial-listings-only-dsl',
};

/**
 * Dedicated Program Finder placement. The tenant flag applies only here.
 * RV config binds the flow with `flowOverrides` keyed by this placement.
 * A tag-level override mounts that flow on the tag's own placement, where
 * this flag does not apply.
 * The flow id is the host's resolved `forcedFlowId`, not a constant in this package.
 */
const PROGRAM_FINDER_PLACEMENT: SlotPlacementId = 'programFinder';

/**
 * Program Finder mounts only on its placement, with a resolved flow id, and
 * with the tenant flag explicitly true. Every other placement ignores the flag.
 * A missing flow id does not fall through to `default-programFinder`.
 */
function isProgramFinderSuppressed(
  placement: SlotPlacementId | undefined,
  forcedFlowId: string | undefined,
  siteConfigs: SiteConfig['configs'] | undefined,
): boolean {
  if (placement !== PROGRAM_FINDER_PLACEMENT) {
    return false;
  }

  if (!forcedFlowId) {
    return true;
  }

  return siteConfigs?.multitenant?.programFinderEnabled !== true;
}

function resolveFlowId(
  forcedFlowId: string,
  placement: SlotPlacementId,
): string {
  if (forcedFlowId) return forcedFlowId;
  return placement
    ? (PLACEMENT_FALLBACK_FLOW_IDS[placement] ?? `default-${placement}`)
    : 'default';
}

const DCS_KEYS = Object.keys(DCS_NAMESPACE) as DcsKey[];

function needsDcsTaxonomySeed(
  steps: Step[],
  dcs: Partial<DCSContext> | undefined,
): boolean {
  if (!dcs) return false;

  const entryStep = steps.find((step) => step.isEntry === true) ?? steps[0];
  const dslConfig = (
    entryStep as
      | (Step & {
          dslConfig?: { type?: unknown; data?: { content?: unknown } };
        })
      | undefined
  )?.dslConfig;
  if (dslConfig?.type !== 'entry') return false;

  const boundComponents = buildBoundComponentIndex(dslConfig.data?.content);
  // Presence of a formKey is not a DSM binding. Only a `_bind.*.source`
  // can supply display labels, so a Select with a formKey and no source
  // still needs the taxonomy seed.
  return DCS_KEYS.some(
    (key) => Boolean(dcs[key]) && !boundComponents[key]?.source,
  );
}

export interface DynamicExperienceBaseProps {
  /**
   * Placement ID for the experience
   */
  placement?: SlotPlacementId;
  /**
   * Force a specific flow ID (bypasses audience intent)
   */
  forcedFlowId?: string;
  /**
   * Which host surface is rendering this experience (`discover`,
   * `degree-finder`, `school-search`, `de-flow`, `shortcode`). Reported to New
   * Relic so DE telemetry can be faceted by surface. `placement` does not serve
   * this purpose — it identifies the slot position, and several surfaces share
   * one placement.
   */
  experienceType?: string;
  /**
   * Jump to a specific step by ID
   */
  jumpToStepId?: string;
  /**
   * DCS context (degree, category, subject)
   */
  dcs?: DCSContext;
  /**
   * Optional customField→slug map received from a Discover host (keys are
   * stripped `ctx_*` handles; values are decoded slugs). Distinct from `dcs`,
   * send-side `entryPoint.forwardFields` / `contextParams`, and legacy
   * `contextualDefaults` (DCS triad). The provider seeds owning-step
   * `flowState` from this map via `initializeAutoPopulation`.
   */
  forwardedContext?: Record<string, string>;
  /**
   * Control values applied once, when the experience initializes.
   * Keys are `formKey`s. Values are strings; a MultiSelect is one
   * comma-separated string of `options[].value`. A later change is ignored.
   */
  initialFormState?: Readonly<Record<string, string>>;
  /**
   * Form-state keys the host wants to hear after mount. The default is an
   * empty list, and an empty list emits nothing. The list is not inferred
   * from `initialFormState`: a key the learner sets after load is reported
   * when it is declared here, even if the landing URL did not include it.
   */
  observedFormStateKeys?: readonly string[];
  /**
   * Fired after a form-state commit when a declared key changed. `formState`
   * contains only `observedFormStateKeys` from that step's snapshot — never
   * the raw snapshot. Each step has its own snapshot, so `stepId` identifies
   * which one. A bound text input emits once per keystroke; the host
   * debounces the keys that need it. A declared key the experience writes
   * itself after mount (the async IP `countryCode` bridge, auto-population)
   * is reported the same way, with no learner action.
   */
  onFormStateChange?: (change: ObservedFormStateChange) => void;
  /** Legacy EduDirect school id(s) from host markup, comma-separated; bound into the offerings `externalIds` filter. */
  schoolId?: string;
  /** Page-authored headline from host markup; exposed to DSL configs as `{{context.headline.*}}`. */
  headline?: HostHeadline;
  /**
   * Generic per-instance params from the embedding surface, exposed to DSL
   * configs as `{{context.params.*}}` (and `_bind` `source: 'context'`,
   * `path: 'params.*'`). Use for reusable/parameterized DSLs that need
   * placement-scoped values that are not DCS dimensions — e.g. a rankings
   * config resolving `"/v1/rankings/{{context.params.listKey}}"`.
   */
  params?: FlowContextParams;
  /**
   * Visitor facts exposed as `{{context.request.*}}` (and `_bind`
   * `{ source: 'context', path: 'request.*' }`). The provider keeps only
   * the five contract fields. Missing fields read as "unknown". This is
   * a snapshot, not headers, cookies, or tokens.
   */
  requestContext?: RequestContextSnapshot;
  /**
   * Rule results the page host resolved for this page: outcomes, statuses,
   * and definition values, as `evaluate-set` returned them. DSL configs read
   * them, read-only, as `{{context.decisions.*}}`, and `platform.variant`
   * blocks render from them with no request. A new object after load
   * re-renders the blocks and text that read a changed entry. Omit it to let
   * variants evaluate through the page's own decision request.
   */
  decisions?: EmbeddedDecisions;
  /**
   * The platform variant this render shows, such as `mobile` or `desktop`.
   * A variant-scoped decision shows the outcome for this variant.
   */
  platformVariant?: string;
  /**
   * Whether this is a full page experience
   */
  isFullPageExp?: boolean;
  /**
   * Style variant for the experience
   */
  styleVariant?: DynamicExperienceVariant;
  /**
   * Entry step ID
   */
  entryStepId?: string;
  /**
   * Starting step index
   */
  fromStepIndex?: number;
  /**
   * Enable debug mode for results
   */
  debugResult?: boolean;
  /**
   * Whether to show placeholder during loading
   */
  needsPlaceholder?: boolean;
  /**
   * When true, DE reserves its placement-sized placeholder but defers the flow
   * fetch. Use this when the host is still resolving the real flow id (e.g. a
   * Monarch slot evaluation) so DE never races a stale `default-${placement}`
   * flow while it waits. The placeholder is reserved from first render (SSR
   * included), so the host can mount DE immediately and avoid the layout shift
   * that comes from deferring the mount itself. Flip to false once the
   * forcedFlowId is known and DE will fetch and render in place.
   */
  awaitingFlowId?: boolean;
  /**
   * Additional props to pass to slot components
   */
  componentProps?: Record<string, unknown>;
  /**
   * Target environment — determines all API endpoint URLs.
   * Defaults to 'prod'.
   */
  environment?: Environment;
  /**
   * Tenant / source key identifier.
   */
  tenantId?: string;
  /**
   * Tenant-level metadata from the consumer (typically derived from the
   * tenants API: `tenant.domain`, `tenant.affiliate`,
   * `configs.urls.appExperienceUrl`, `configs.tracking.mobius.identityToken`,
   * `configs.tracking.cohesion.tagular.sourceKey`). When supplied, these
   * fields are threaded into the internal `SiteConfig` so URL building
   * (`generateResultUrl`) and downstream tracking / Mobius auth see the
   * same values the host app does.
   *
   * Why widen this beyond `domain` + `affiliate`: the legacy shape only
   * covered DSL `discover` exit-on-submit routing. The STUDY_MATCH branch
   * in `generateResultUrl` needs `appExperienceUrl` to build the
   * studymatch destination -- without it `getUrlQuery` throws and the
   * click silently falls through to the discover/voyager fallback even
   * when the entry COMPONENT's `branchingLogic` matched a STUDY_MATCH
   * RESULT branch.
   *
   * The real tenants API returns `affiliate` as a single string --
   * present iff the tenant is an affiliate (e.g. "US News"), absent for
   * owned-and-operated tenants. All fields are optional; populate the
   * ones your host already resolves and leave the rest undefined.
   */
  tenant: {
    /**
     * Tagular source key (`src_...`). **Required.** Dynamic Flows keys
     * tenant-scoped flows under this value, NOT the domain slug, so every
     * consumer must thread it through — otherwise the flow request falls back
     * to the shared `default` tenant and the tenant's own flow is never
     * loaded. This is the value used as the `{tenantId}` path segment when
     * fetching the flow. The remaining fields stay optional.
     */
    tagularSourceKey: string;
    domain?: string;
    /**
     * Publisher display / brand name (e.g. "Psychology.org") from the tenants
     * API `tenant.name`. Threaded onto the internal `SiteConfig.displayName`
     * and exposed to DSL templates as `{{context.tenant.name}}`. Optional.
     */
    displayName?: string;
    affiliate?: string;
    appExperienceUrl?: string;
    identityToken?: string;
    /**
     * Tagular `apiKey` (a UUID, e.g. the Cohesion tenant identity from
     * `configs.tracking.cohesion.tagular.apiKey`). Threaded into the internal
     * `SiteConfig` and used as `trackingContext.tenantId` on the
     * campaign-management v2 conversion request, which validates that field as
     * a UUID. Distinct from the slug-based top-level `tenantId`. Optional: when
     * absent the conversion's optional `trackingContext.tenantId` is omitted.
     */
    tagularApiKey?: string;
  };
  /**
   * Tenant config blob from the host (e.g. hen-site `siteConfig.configs`).
   * Threaded into `ExperienceConfigProvider` so placement min-heights and
   * other tenant-scoped DE settings resolve from Admin / Tenants API.
   */
  siteConfigs?: SiteConfig['configs'];
  /**
   * Additional custom dimensions for analytics events
   */
  customDimensions?: CustomDimensions;
  /**
   * Invoked when flow-fetch fails. Recoverable signal for hosts (e.g. an
   * affiliate-bundle wrapper) that want to log to their own observability
   * channel. Does not replace the fallback render — see
   * `onFallbackShouldRender`.
   */
  onError?: (error: Error) => void;
  /**
   * Invoked when flow-fetch fails, or when the Program Finder flow is
   * suppressed (missing or disabled tenant flag), and DE would otherwise
   * render nothing. The returned ReactNode is rendered in place of the
   * experience, letting hosts surface a contextual fallback (skeleton, retry
   * CTA, hide slot). If omitted, DE renders `null` instead.
   */
  onFallbackShouldRender?: (state: { reason: 'no-flow' }) => React.ReactNode;
  /**
   * Monarch slot / experience identity. Threaded from the host embed's
   * resolved slot placement onto the event context so product / form / element
   * events carry `slotName` / `slotVariant` / `experienceName` (and the
   * in-package-derived `experienceVariant`). `slotId` is the `placement`. All
   * optional — hosts that don't resolve Monarch leave them undefined and the
   * dimensions emit empty.
   */
  slotName?: string;
  slotVariant?: string;
  experienceName?: string;
  /**
   * When true, mounts Radix UI portals (Select, Dialog, Tooltip, Drawer)
   * inside a `[data-de-portal]` container provided by `ExperiencePlacement`
   * so portaled overlays receive styles from the scoped stylesheet
   * (`@highereducation/dynamic-experience/styles/scoped`) and stay within
   * the host's CSS scope (e.g. an affiliate-bundle Shadow-DOM host).
   * Leave false (default) when using the unscoped stylesheet.
   */
  scoped?: boolean;
  /**
   * Explicit container for Radix portals (Select, Tooltip, Dialog, Drawer),
   * threaded through to `ExperiencePlacement`. When provided together with
   * `scoped`, overlays portal here instead of the in-flow `[data-de-portal]`
   * container. The affiliate-bundle host passes a body-level overlay shadow
   * root so overlays escape host list-item stacking contexts.
   * Omit on hen-site — the in-flow container behavior is then unchanged.
   */
  portalContainer?: HTMLElement | null;
  /**
   * When provided, called instead of rendering the built-in results UI when the
   * flow reaches a RESULT step. Receives the result step config and the user's
   * collected flow answers. Supplying this prop also suppresses all API calls
   * that the built-in ResultsPage makes on mount (getQualifiedResults, lead
   * submission setup, etc.) because the Results component tree never renders.
   */
  onResult?: (
    result: Step & StepResult,
    context: { flowState: FlowState; steps: Step[] },
  ) => void;
  /**
   * SSR-prefetched DCS taxonomy (slug→label) as a serializable entries array.
   * When supplied, the taxonomy resolver is ready at first paint. When omitted,
   * the runtime fetches the DCS-scoped seed only for entry flows that have
   * contextual DCS values without a bound Select; bound Select flows continue
   * to resolve labels through the DataSourceManager.
   */
  taxonomySeed?: TaxonomySeed;
  /**
   * SSR-prefetched flow steps. When supplied, the component renders the flow
   * on its very first render — including on the server — instead of showing
   * the placeholder until the client fetch resolves. Server routes that know
   * their flow id before render should prefetch via `fetchFlowSteps`.
   *
   * Only for hosts whose flow id is known server-side. Placements that resolve
   * the flow id from Monarch in the browser must omit it and keep the existing
   * `awaitingFlowId` path.
   *
   * Seeding also skips the client fetch, and with it the DCS taxonomy fallback
   * that runs alongside it. A seeding host that auto-populates DCS owns the
   * taxonomy prefetch too and should pass `taxonomySeed`.
   */
  initialSteps?: Step[];
}

export type DynamicExperienceProps = DynamicExperienceBaseProps;

export type { ObservedFormStateChange };

/**
 * DynamicExperience - Main entry point component
 *
 * This component fetches flow data and renders the dynamic experience.
 * It handles ExperienceConfigProvider internally.
 *
 * @example
 * ```tsx
 * import { DynamicExperience } from '@he/dynamic-experience';
 *
 * <DynamicExperience
 *   environment="int"
 *   tenantId="my-tenant"
 *   tenant={{ tagularSourceKey: 'src_...' }}
 *   placement="aboveTheFold"
 * />
 * ```
 */
export const DynamicExperience: React.FC<DynamicExperienceProps> = ({
  placement = '',
  forcedFlowId = '',
  experienceType,
  jumpToStepId = '',
  entryStepId = '',
  fromStepIndex = 1,
  dcs,
  forwardedContext,
  initialFormState,
  observedFormStateKeys = [],
  onFormStateChange,
  schoolId,
  headline,
  params,
  requestContext,
  decisions,
  platformVariant,
  isFullPageExp = false,
  styleVariant = 'default',
  debugResult = false,
  needsPlaceholder = true,
  awaitingFlowId = false,
  componentProps,
  environment = 'prod',
  tenantId,
  tenant,
  siteConfigs,
  customDimensions,
  onError,
  onFallbackShouldRender,
  slotName,
  slotVariant,
  experienceName,
  scoped = false,
  portalContainer,
  onResult,
  taxonomySeed,
  initialSteps,
}) => {
  const programFinderSuppressed = isProgramFinderSuppressed(
    placement,
    forcedFlowId,
    siteConfigs,
  );

  const config: ExperienceConfig = useMemo(() => {
    const productApiBffOrigin = resolveProductApiBffOrigin(tenantId);
    return {
      api: {
        ...getApiConfigForEnv(environment),
        // O&O tenants call the lead BFF same-origin; affiliate embeds run on a
        // foreign origin and must target the absolute hen-site origin.
        leadApiBaseUrl: resolveLeadApiBaseUrl(environment, {
          affiliate: Boolean(tenant.affiliate),
        }),
        // Same story for the location-inference BFF (`GET /api/v1/locate`): O&O
        // resolves it same-origin, but an affiliate embed must hit the absolute
        // hen-site origin or the relative path 404s on the affiliate host.
        locationInferenceApiUrl: resolveLocationInferenceApiUrl(environment, {
          affiliate: Boolean(tenant.affiliate),
        }),
        // Relative on purpose: the Product API BFF is mounted on the edX host
        // (`/api/product-api`), not on hen-site. There is no absolute fallback.
        // A partner page, hen-site preview, or local host without that route
        // will 404.
        ...(productApiBffOrigin ? { productApiBffOrigin } : {}),
      },
      tenantId,
      tracking: {
        dynamicFlowEventFlag: false,
        tagularSourceKey: tenant.tagularSourceKey,
      },
      site: {
        tenantId,
        domain: tenant.domain,
        displayName: tenant.displayName,
        affiliate: tenant.affiliate,
        appExperienceUrl: tenant.appExperienceUrl,
        identityToken: tenant.identityToken,
        tagularSourceKey: tenant.tagularSourceKey,
        tagularApiKey: tenant.tagularApiKey,
        configs: siteConfigs,
      },
    };
  }, [environment, tenantId, tenant, siteConfigs]);

  const [steps, setSteps] = useState<Step[] | null>(initialSteps ?? null);
  const [resolvedTaxonomySeed, setResolvedTaxonomySeed] = useState<
    TaxonomySeed | undefined
  >(taxonomySeed);
  const [audienceIntent] = useState<DecisionProfileResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(!initialSteps);
  const [correlationId] = useState(() => uuid());
  const [elementLoadedCorrelationId] = useState(() => uuid());

  // Fetch flow data
  useEffect(() => {
    // The host prefetched the flow on the server, so the steps are already in
    // state from the first render. Re-fetching would replace identical data
    // and discard any answers collected before it resolved.
    if (initialSteps) {
      return;
    }

    // Hold in the placeholder state until the host resolves the flow id. Firing
    // the fetch now would resolve `default-${placement}` and race the real flow.
    if (awaitingFlowId) {
      return;
    }

    // Program Finder is fail-closed: this placement must not reach Dynamic
    // Flows unless the tenant flag is on and the host resolved a flow id.
    if (programFinderSuppressed) {
      return;
    }

    let cancelled = false;

    const fetchFlowData = async () => {
      setIsLoading(true);
      setError(null);

      // Dynamic Flows keys tenant-scoped flows under the tagular source key
      // (src_...), not the domain slug. The source key is required on the
      // `tenant` prop; resolve the flow tenant from it (the `tenantId` slug
      // and 'default' remain only as defensive guards against an empty key).
      // Hoisted out of the try so a fetch failure can still name the flow
      // and tenant in New Relic.
      const sourceKey = config.site?.tagularSourceKey;
      const resolvedTenantId = resolveFlowTenantId(sourceKey, config.tenantId);
      const flowId = resolveFlowId(forcedFlowId, placement);

      try {
        // Fail loud, not silent: a missing source key means the request falls
        // back to the slug or the shared `default` tenant and the tenant's own
        // flow never loads. Warn so a misconfigured tenant is observable rather
        // than silently degrading (mirrors the shortcode adapter's fail-safe).
        if (!sourceKey) {
          console.warn(
            `[DynamicExperience] Missing tenant.tagularSourceKey; falling back ` +
              `to "${resolvedTenantId}" for flow resolution. The tenant-scoped ` +
              `flow will not load — verify the tenant config exposes ` +
              `cohesion.tagular.sourceKey.`,
          );
        }

        // Try to get audience intent if no forced flow ID
        if (!forcedFlowId && config.tracking?.tagularSourceKey) {
          // Could fetch audience intent here if needed
        }

        // Tag the browser session with the resolved flow, so every later
        // PageView / PageAction / JavaScriptError / AjaxRequest on this page
        // can be faceted by flow. Set here rather than in the host wrappers
        // because the id isn't final until the forced/placement fallback
        // chain above resolves.
        newRelicCustomAttribute('flowId', flowId);
        if (experienceType) {
          newRelicCustomAttribute('experienceType', experienceType);
        }

        // Fetch flow steps
        const result = await getFlow(resolvedTenantId, flowId);

        if (result.error) {
          throw new Error(result.error);
        }

        if (result.data) {
          const nextSteps = result.data;
          let nextTaxonomySeed = taxonomySeed;
          const effectiveDcs = applyEntryPointDcsDefaults(
            dcs,
            getEntryPointConfigFromSteps(nextSteps),
          );

          if (
            taxonomySeed === undefined &&
            config.api.offeringsServiceBaseUrl &&
            needsDcsTaxonomySeed(nextSteps, effectiveDcs)
          ) {
            try {
              nextTaxonomySeed = await fetchTaxonomySeed(
                config.api.offeringsServiceBaseUrl,
                { scope: 'dcs' },
              );
            } catch {
              nextTaxonomySeed = [];
            }
          }

          if (cancelled) return;
          setResolvedTaxonomySeed(nextTaxonomySeed);
          setSteps(nextSteps);
        }
      } catch (err) {
        if (cancelled) return;
        const fetchError =
          err instanceof Error ? err : new Error('Failed to fetch flow data');
        setError(fetchError);
        // A flow that never loads renders nothing and emits no step events, so
        // it is invisible to any volume- or rate-based alert. Report it as a
        // positive signal instead of an absence.
        newRelicNoticeError(fetchError, {
          source: 'DynamicExperience',
          phase: 'fetchFlow',
          flowId: flowId ?? 'unresolved',
          tenantId: resolvedTenantId,
        });
        console.error('Error fetching dynamic experience data:', fetchError);
        onError?.(fetchError);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchFlowData();
    return () => {
      cancelled = true;
    };
  }, [
    initialSteps,
    awaitingFlowId,
    programFinderSuppressed,
    forcedFlowId,
    experienceType,
    placement,
    config.tenantId,
    config.tracking?.tagularSourceKey,
    config.api.offeringsServiceBaseUrl,
    dcs?.degree,
    dcs?.category,
    dcs?.subject,
    taxonomySeed,
    onError,
  ]);

  if (programFinderSuppressed) {
    return onFallbackShouldRender?.({ reason: 'no-flow' }) ?? null;
  }

  if (error) {
    return onFallbackShouldRender?.({ reason: 'no-flow' }) ?? null;
  }

  // Placeholder state: while the host is still resolving the flow id, while the
  // flow is being fetched, or when no steps resolved. Reserving the
  // placement-sized box here (from first render, SSR included) is what prevents
  // the layout shift — the host mounts DE immediately rather than deferring.
  if (awaitingFlowId || isLoading || !steps || steps.length === 0) {
    return (
      <ExperienceConfigProvider config={config}>
        <ExperiencePlacement
          placementId={placement}
          needsPlaceholder={needsPlaceholder}
          scoped={scoped}
          portalContainer={portalContainer}
        >
          <></>
        </ExperiencePlacement>
      </ExperienceConfigProvider>
    );
  }

  return (
    <ExperienceConfigProvider config={config}>
      <TaxonomyProvider seed={resolvedTaxonomySeed}>
        <ObservedFormStateProvider
          observedFormStateKeys={observedFormStateKeys}
          onFormStateChange={onFormStateChange}
        >
          <DynamicExperienceProvider
            steps={steps}
            correlationId={correlationId}
            placementId={placement}
            jumpToStepId={jumpToStepId}
            dcs={dcs}
            forwardedContext={forwardedContext}
            initialFormState={initialFormState}
            schoolId={schoolId}
            headline={headline}
            params={params}
            requestContext={requestContext}
            decisions={decisions}
            platformVariant={platformVariant}
            entryStepId={entryStepId}
            isFullPageExp={isFullPageExp}
            audienceIntent={audienceIntent}
            styleVariant={styleVariant}
            fromStepIndex={fromStepIndex}
            elementLoadedCorrelationId={elementLoadedCorrelationId}
            debugResult={debugResult}
            customDimensions={customDimensions}
            slotName={slotName}
            slotVariant={slotVariant}
            experienceName={experienceName}
          >
            <ExperiencePlacement
              needsPlaceholder={needsPlaceholder}
              placementId={placement}
              scoped={scoped}
              portalContainer={portalContainer}
              // The flow has resolved and the experience renders synchronously
              // below, so release the placeholder min-height — otherwise
              // experiences shorter than the reserved height keep permanent
              // whitespace under them.
              contentLoaded
            >
              <Experience componentProps={componentProps} onResult={onResult} />
            </ExperiencePlacement>
          </DynamicExperienceProvider>
        </ObservedFormStateProvider>
      </TaxonomyProvider>
    </ExperienceConfigProvider>
  );
};

export default DynamicExperience;
